import React from 'react';
import { isIOS } from 'react-device-detect';
import {
    iOSResponsiveContainerHeight,
    widgetTypes,
} from '../../shared/constants/constants';
import GaugeValueDisplayWrapper from './valueDisplay/gaugeValueDisplay/GaugeValueDisplayWrapper';
import CalculatedGaugeValueDisplayWrapper from './valueDisplay/gaugeValueDisplay/CalculatedGaugeValueDisplayWrapper';
import GaugeBarGraphWrapper from './charts/barCharts/GaugeBarGraphWrapper';
import GaugeBiaxialBarWithLineGraphWrapper from './charts/composedCharts/GaugeBiaxialBarWithLineGraphWrapper';
import GaugeLineGraphWrapper from './charts/lineCharts/GaugeLineGraphWrapper';
import GaugePieChartWrapper from './charts/gaugePieChart/GaugePieChartWrapper';
import TimeSeriesBarGraphWrapper from './charts/barCharts/TimeSeriesBarGraphWrapper';
import TimeSeriesBiaxialLineGraphWrapper from './charts/lineCharts/TimeSeriesBiaxialLineGraphWrapper';
import TimeSeriesBiaxialBarWithLineGraphWrapper from './charts/composedCharts/TimeSeriesBiaxialBarWithLineGraphWrapper';
import TimeSeriesLineGraphWrapper from './charts/lineCharts/TimeSeriesLineGraphWrapper';
import ThresholdValueDisplayWrapper from './valueDisplay/thresholdValueDisplay/ThresholdDisplayWrapper';
import TimeSeriesHeatmapWrapper from './lists/timeSeriesHeatMap/TimeSeriesHeatmapWrapper';
import MultiChartWrapper from './charts/multiChart/MultiChartWrapper';
import NumberWithIndicatorWrapper from './valueDisplay/numberWithIndicator/NumberWithIndicatorWrapper';
import CustomizableTableWrapper from './customizableTable/CustomizableTableWrapper';
import LinkListWrapper from './lists/linkList/LinkListWrapper';
import AirportStatusMapWrapper from './airportStatusMap/AirportStatusMapWrapper';
import SparklineWrapper from './lists/sparkLine/SparklineWrapper';
import GaugeDialWrapper from './valueDisplay/gaugeDial/GaugeDialWrapper';
import RadarChartWrapper from './charts/radarChart/RadarChartWrapper';
import StatusCellsWrapper from './valueDisplay/statusCells/StatusCellsWrapper';
import SystemLinks from '../../landingPage/systemPanels/SystemLinks';
import UserFavorites from '../../landingPage/systemPanels/UserFavorites';
import SankeyChartWrapper from './charts/sankeyChart/SankeyChartWrapper';
import GaugeComposedBarLineGraphWrapper from './charts/composedCharts/GaugeComposedBarLineGraphWrapper';
import TimeSeriesComposedBarLineGraphWrapper from './charts/composedCharts/TimeSeriesComposedBarLineGraphWrapper';
import PositiveNegativeBarGraphWrapper from './charts/barCharts/PositiveNegativeBarGraphWrapper';
import GaugeTicketWrapper from './lists/gaugeTicket/GaugeTicketWrapper';
import NewsfeedWrapper from './lists/newsFeed/NewsfeedWrapper';
import FlightTableWrapper from '../../customizations/smartBag/widgets/flightTables/FlightTableWrapper';
import StationTableWrapper from '../../customizations/smartBag/widgets/stationTables/StationTableWrapper';
import InboundFlightTable from '../../customizations/smartBag/widgets/flightTables/InboundFlightTable';
import OutboundFlightTable from '../../customizations/smartBag/widgets/flightTables/OutboundFlightTable';
import InboundStationTable from '../../customizations/smartBag/widgets/stationTables/InboundStationTable';
import OutboundStationTable from '../../customizations/smartBag/widgets/stationTables/OutboundStationTable';
import NationalKpisWrapper from './valueDisplay/ua/nationalKpi/NationKpisWrapper';
import StringWithIndicatorWrapper from './valueDisplay/ua/stringWithIndicator/StringWithIndicatorWrapper';

/* eslint-disable react/jsx-props-no-spreading */

const responsiveContainerProperties = {
    height: isIOS ? iOSResponsiveContainerHeight : '99%',
};
const legendProperties = { wrapperStyle: { bottom: 5 } };
const xAxisProperties = {
    angle: 0,
    textAnchor: 'middle',
    height: 40,
};
const yAxisProperties = {
    allowDecimals: false,
};
const referenceLineProperties = {
    strokeDasharray: '3 3',
    ifOverflow: 'extendDomain',
};

const drawConfigs = {
    airportStatusMap: {
        key: 'airportStatusMap',
        render: (widgetProps) => <AirportStatusMapWrapper {...widgetProps} />,
        hasChart: false,
    },
    calculatedGaugeValueDisplay: {
        key: 'calculatedGaugeValueDisplay',
        type: widgetTypes.gauge,
        render: (widgetProps) => (
            <CalculatedGaugeValueDisplayWrapper {...widgetProps} />
        ),
        hasChart: false,
    },
    customizableTable: {
        key: 'customizableTable',
        type: widgetTypes.table,
        render: (widgetProps) => <CustomizableTableWrapper {...widgetProps} />,
    },
    gaugeBarGraph: {
        key: 'gaugeBarGraph',
        type: widgetTypes.barGraph,
        render: (widgetProps) => <GaugeBarGraphWrapper {...widgetProps} />,
        barChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        showSinglePointTooltip: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    gaugeBiaxialBarWithLineGraph: {
        key: 'gaugeBiaxialBarWithLineGraph',
        type: widgetTypes.barGraph,
        render: (widgetProps) => (
            <GaugeBiaxialBarWithLineGraphWrapper {...widgetProps} />
        ),
        barMetricRequestId: 0,
        lineMetricRequestId: 1,
        composedChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        showSinglePointTooltip: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    gaugeComposedBarLineGraph: {
        key: 'gaugeComposedBarLineGraph',
        type: widgetTypes.barGraph,
        render: (widgetProps) => (
            <GaugeComposedBarLineGraphWrapper {...widgetProps} />
        ),
        barMetricRequestId: 0,
        lineMetricRequestId: 1,
        composedChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        showSinglePointTooltip: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    gaugeDial: {
        key: 'gaugeDial',
        type: widgetTypes.gauge,
        render: (widgetProps) => <GaugeDialWrapper {...widgetProps} />,
    },
    gaugeLineGraph: {
        key: 'gaugeLineGraph',
        type: widgetTypes.lineGraph,
        render: (widgetProps) => <GaugeLineGraphWrapper {...widgetProps} />,
        lineChartProperties: {
            syncId: null,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    gaugePieChart: {
        key: 'gaugePieChart',
        type: widgetTypes.pieChart,
        render: (widgetProps) => <GaugePieChartWrapper {...widgetProps} />,
        pieChartProperties: {},
        pieProperties: {
            isAnimationActive: false,
            label: true,
        },
        legendProperties,
        responsiveContainerProperties,
        showLegend: true,
        tooltipProperties: {},
    },
    gaugeTicket: {
        key: 'gaugeTicket',
        render: (widgetProps) => <GaugeTicketWrapper {...widgetProps} />,
        hasChart: false,
    },
    gaugeValueDisplay: {
        key: 'gaugeValueDisplay',
        type: widgetTypes.gauge,
        render: (widgetProps) => <GaugeValueDisplayWrapper {...widgetProps} />,
        hasChart: false,
    },
    linkList: {
        key: 'linkList',
        type: widgetTypes.selector,
        render: (widgetProps) => <LinkListWrapper {...widgetProps} />,
        hasChart: false,
    },
    multiChart: {
        key: 'multiChart',
        type: widgetTypes.lineGraph,
        render: (widgetProps) => <MultiChartWrapper {...widgetProps} />,
        chartType: 'timeSeriesLineGraph',
    },
    newsfeed: {
        key: 'newsfeed',
        render: (widgetProps) => <NewsfeedWrapper {...widgetProps} />,
        hasChart: true,
    },
    numberWithIndicator: {
        key: 'numberWithIndicator',
        type: widgetTypes.numberWithIndicator,
        render: (widgetProps) => (
            <NumberWithIndicatorWrapper {...widgetProps} />
        ),
        hasChart: false,
    },
    stringWithIndicator: {
        key: 'stringWithIndicator',
        type: widgetTypes.numberWithIndicator,
        render: (widgetProps) => (
            <StringWithIndicatorWrapper {...widgetProps} />
        ),
        hasChart: false,
    },
    positiveNegativeBarGraph: {
        key: 'positiveNegativeBarGraph',
        type: widgetTypes.selector,
        render: (widgetProps) => (
            <PositiveNegativeBarGraphWrapper {...widgetProps} />
        ),
        barChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        responsiveContainerProperties,
        showSinglePointTooltip: true,
    },
    radarChart: {
        key: 'radarChart',
        render: (widgetProps) => <RadarChartWrapper {...widgetProps} />,
        legendProperties,
        polarAngleAxisProperties: {},
        polarGridProperties: {},
        polarRadiusAxisProperties: {},
        radarChartProperties: {},
        radarProperties: {
            fillOpacity: 0.4,
            isAnimationActive: false,
        },
        responsiveContainerProperties,
        showRadiusAxis: false,
        showLegend: true,
    },
    sankeyChart: {
        key: 'sankeyChart',
        type: widgetTypes.sankey,
        render: (widgetProps) => <SankeyChartWrapper {...widgetProps} />,
    },
    timeSeriesBarGraph: {
        key: 'timeSeriesBarGraph',
        type: widgetTypes.barGraph,
        render: (widgetProps) => <TimeSeriesBarGraphWrapper {...widgetProps} />,
        barChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        showSinglePointTooltip: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    timeSeriesBiaxialLineGraph: {
        key: 'timeSeriesBiaxialLineGraph',
        type: widgetTypes.lineGraph,
        render: (widgetProps) => (
            <TimeSeriesBiaxialLineGraphWrapper {...widgetProps} />
        ),
        leftAxisMetricRequestId: 0,
        rightAxisMetricRequestId: 1,
        lineChartProperties: {
            syncId: null,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    timeSeriesBiaxialBarWithLineGraph: {
        key: 'timeSeriesBiaxialBarWithLineGraph',
        type: widgetTypes.barGraph,
        render: (widgetProps) => (
            <TimeSeriesBiaxialBarWithLineGraphWrapper {...widgetProps} />
        ),
        barMetricRequestId: 0,
        lineMetricRequestId: 1,
        composedChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        showSinglePointTooltip: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    timeSeriesComposedBarLineGraph: {
        key: 'timeSeriesComposedBarLineGraph',
        type: widgetTypes.barGraph,
        render: (widgetProps) => (
            <TimeSeriesComposedBarLineGraphWrapper {...widgetProps} />
        ),
        barMetricRequestId: 0,
        lineMetricRequestId: 1,
        composedChartProperties: {
            syncId: null,
        },
        barProperties: {
            isAnimationActive: false,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        showSinglePointTooltip: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    timeSeriesLineGraph: {
        key: 'timeSeriesLineGraph',
        type: widgetTypes.lineGraph,
        render: (widgetProps) => (
            <TimeSeriesLineGraphWrapper {...widgetProps} />
        ),
        lineChartProperties: {
            syncId: null,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend: true,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    sparkline: {
        key: 'sparkline',
        type: widgetTypes.sparkline,
        render: (widgetProps) => <SparklineWrapper {...widgetProps} />,
        disconnectedGraph: true,
        lineChartProperties: {
            syncId: null,
        },
        lineProperties: {
            isAnimationActive: false,
        },
        referenceLineProperties,
        responsiveContainerProperties,
        tooltipProperties: {},
        xAxisProperties,
        yAxisProperties,
    },
    statusCells: {
        key: 'statusCells',
        type: widgetTypes.statusCells,
        render: (widgetProps) => <StatusCellsWrapper {...widgetProps} />,
    },
    thresholdValueDisplay: {
        key: 'thresholdValueDisplay',
        render: (widgetProps) => (
            <ThresholdValueDisplayWrapper {...widgetProps} />
        ),
    },
    timeSeriesHeatmap: {
        key: 'timeSeriesHeatmap',
        render: (widgetProps) => <TimeSeriesHeatmapWrapper {...widgetProps} />,
        colorScheme: 'interpolateYlGnBu',
        showLegend: true,
        valueRange: {},
    },
    outboundFlightTable: {
        key: 'outboundFlightTable',
        render: (widgetProps) => (
            <FlightTableWrapper
                {...widgetProps}
                TableComponent={OutboundFlightTable}
                isOutbound={true}
            />
        ),
    },
    inboundFlightTable: {
        key: 'inboundFlightTable',
        render: (widgetProps) => (
            <FlightTableWrapper
                {...widgetProps}
                TableComponent={InboundFlightTable}
                isOutbound={false}
            />
        ),
    },
    outboundStationTable: {
        key: 'outboundStationTable',
        render: (widgetProps) => (
            <StationTableWrapper
                {...widgetProps}
                StationComponent={OutboundStationTable}
                isOutbound={true}
            />
        ),
    },
    inboundStationTable: {
        key: 'inboundStationTable',
        render: (widgetProps) => (
            <StationTableWrapper
                {...widgetProps}
                StationComponent={InboundStationTable}
                isOutbound={false}
            />
        ),
    },
    nationalKpis: {
        key: 'nationalKpis',
        render: (widgetProps) => <NationalKpisWrapper {...widgetProps} />,
        summaryRequestId: 0,
        kpisRequestId: 1,
    },
};

const modifiedVersions = {
    gaugeBarGraphStacked: {
        ...drawConfigs.gaugeBarGraph,
        key: 'gaugeBarGraphStacked',
        barProperties: {
            ...drawConfigs.gaugeBarGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
    },
    gaugeBarGraphSideways: {
        ...drawConfigs.gaugeBarGraph,
        key: 'gaugeBarGraphSideways',
        barChartProperties: {
            ...drawConfigs.gaugeBarGraph.barChartProperties,
            layout: 'vertical',
        },
        showSinglePointTooltip: true,
        xAxisProperties: {
            ...drawConfigs.gaugeBarGraph.xAxisProperties,
            type: 'number',
            allowDecimals: false,
        },
        yAxisProperties: {
            ...drawConfigs.gaugeBarGraph.yAxisProperties,
            type: 'category',
        },
    },
    gaugeBarGraphStackedSideways: {
        ...drawConfigs.gaugeBarGraph,
        key: 'gaugeBarGraphStackedSideways',
        barChartProperties: {
            ...drawConfigs.gaugeBarGraph.barChartProperties,
            layout: 'vertical',
        },
        barProperties: {
            ...drawConfigs.gaugeBarGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
        xAxisProperties: {
            ...drawConfigs.gaugeBarGraph.xAxisProperties,
            type: 'number',
            allowDecimals: false,
        },
        yAxisProperties: {
            ...drawConfigs.gaugeBarGraph.yAxisProperties,
            type: 'category',
        },
    },
    gaugeBiaxialBarWithLineGraphStacked: {
        ...drawConfigs.gaugeBiaxialBarWithLineGraph,
        key: 'gaugeBiaxialBarWithLineGraphStacked',
        barProperties: {
            ...drawConfigs.gaugeBiaxialBarWithLineGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
    },
    gaugeComposedBarLineGraphStacked: {
        ...drawConfigs.gaugeComposedBarLineGraph,
        key: 'gaugeComposedBarLineGraphStacked',
        barProperties: {
            ...drawConfigs.gaugeComposedBarLineGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
    },
    timeSeriesBarGraphStacked: {
        ...drawConfigs.timeSeriesBarGraph,
        key: 'timeSeriesBarGraphStacked',
        barProperties: {
            ...drawConfigs.timeSeriesBarGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
    },
    timeSeriesBiaxialBarWithLineGraphStacked: {
        ...drawConfigs.timeSeriesBiaxialBarWithLineGraph,
        key: 'timeSeriesBiaxialBarWithLineGraphStacked',
        barProperties: {
            ...drawConfigs.timeSeriesBiaxialBarWithLineGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
    },
    timeSeriesComposedBarLineGraphStacked: {
        ...drawConfigs.timeSeriesComposedBarLineGraph,
        key: 'timeSeriesComposedBarLineGraphStacked',
        barProperties: {
            ...drawConfigs.timeSeriesComposedBarLineGraph.barProperties,
            stackId: 'barStack',
        },
        showSinglePointTooltip: false,
    },
};

const systemPanelWidgets = {
    systemLinks: {
        key: 'systemLinks',
        render: (widgetProps) => <SystemLinks {...widgetProps} />,
    },
    userFavorites: {
        key: 'userFavorites',
        render: (widgetProps) => <UserFavorites {...widgetProps} />,
    },
};

export default {
    ...drawConfigs,
    ...modifiedVersions,
    ...systemPanelWidgets,
};
